import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/components/data-display/badge'
import { ErrorState } from '@/components/feedback/error-state'
import { useFeedback } from '@/components/feedback/feedback-context'
import { InlineNotice } from '@/components/feedback/inline-notice'
import { Skeleton } from '@/components/feedback/skeleton'
import { HelpTooltip } from '@/components/overlays/help-tooltip'
import { Button } from '@/components/primitives/button'
import { ApiError } from '@/lib/api/http-client'
import { readStoredPortalAccount } from '@/lib/auth/session-storage'

import {
  AgentToolSelectionError,
  agentToolIdentityKey,
  buildAgentToolBindingsPayload,
  getCoreAgentToolBindings,
  getSelfAgentToolBindings,
  updateCoreAgentToolBindings,
  updateSelfAgentToolBindings,
  type AgentToolBindings,
  type AgentToolBindingsUpdatePayload,
  type AgentToolOption,
} from './api'
import { agentToolsErrorMessage } from './errors'
import { canWriteAgentTools } from './permissions'
import { ToolBindingDrawer, ToolOptionRow } from './tool-binding-drawer'
import { ToolSourceMark } from './tool-source-mark'

export type AgentToolBindingOwner =
  | { agentId: 'core'; agentType: 'core_agent' }
  | { agentId: string; agentType: 'self_agent' }

export interface AgentToolBindingCheckboxesProps {
  owner: AgentToolBindingOwner
}

export interface AgentToolDraftSelectorProps {
  error?: unknown
  isLoading: boolean
  onRetry: () => void
  onSelectedChange: (selected: Set<string>) => void
  selected: ReadonlySet<string>
  tools: AgentToolOption[]
}

interface SaveBindingVariables {
  owner: AgentToolBindingOwner
  payload: AgentToolBindingsUpdatePayload
}

interface SelectedToolGroup {
  key: string
  label: string
  source: AgentToolOption['source']
  tools: AgentToolOption[]
}

function ownerQueryKey(owner: AgentToolBindingOwner) {
  return ['admin-agent-tool-bindings', owner.agentType, owner.agentId] as const
}

function selectedState(bindings: AgentToolBindings) {
  return new Set(
    bindings.tools.filter((tool) => tool.selected).map((tool) => agentToolIdentityKey(tool)),
  )
}

function sameSet(left: ReadonlySet<string>, right: ReadonlySet<string>) {
  return left.size === right.size && [...left].every((item) => right.has(item))
}

function bindingDraftKey(bindings: AgentToolBindings, owner: AgentToolBindingOwner) {
  const toolState = bindings.tools.map((tool) =>
    [
      agentToolIdentityKey(tool),
      tool.definition_id ?? '',
      tool.bound_definition_id ?? '',
      tool.schema_hash,
      tool.name,
      String(tool.selected),
      String(tool.review_required),
      String(tool.available),
      tool.unavailable_reason ?? '',
    ].join(':'),
  )

  return [owner.agentType, owner.agentId, bindings.selection_revision, ...toolState].join('|')
}

function BindingHeading({
  action,
  compact = false,
  reviewCount,
  selectedCount,
}: {
  action?: ReactNode
  compact?: boolean
  reviewCount: number
  selectedCount: number
}) {
  const { t } = useTranslation('adminAgentTools')
  const HeadingTag = compact ? 'h4' : 'h2'

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <div className="flex items-center gap-2">
          <HeadingTag
            className={
              compact
                ? 'text-sm font-semibold text-[var(--color-text)]'
                : 'text-[15px] font-semibold text-[var(--color-text)]'
            }
          >
            {t('bindings.selectionTitle')}
          </HeadingTag>
          <HelpTooltip label={t('bindings.exactBindingHelpLabel')} widthClassName="w-[320px]">
            {t('bindings.exactBindingNotice')}
          </HelpTooltip>
        </div>
        <p className="mt-1 text-sm text-[var(--color-text-muted)]">
          {t('bindings.selectionDescription')}
        </p>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <span className="text-xs text-[var(--color-text-muted)]">
          {t('bindings.summary.selected', { count: selectedCount })}
        </span>
        {reviewCount > 0 ? (
          <Badge variant="warning">
            {t('bindings.summary.reviewRequired', { count: reviewCount })}
          </Badge>
        ) : null}
        {action}
      </div>
    </div>
  )
}

export function AgentToolBindingCheckboxes({ owner }: AgentToolBindingCheckboxesProps) {
  const { t } = useTranslation('adminAgentTools')
  const canWrite = canWriteAgentTools(readStoredPortalAccount('admin').role)

  const bindingsQuery = useQuery({
    queryFn: () =>
      owner.agentType === 'self_agent'
        ? getSelfAgentToolBindings(owner.agentId)
        : getCoreAgentToolBindings(),
    queryKey: ownerQueryKey(owner),
  })

  if (bindingsQuery.isLoading) {
    return (
      <div aria-label={t('bindings.workspaceLabel')} className="grid gap-4">
        <BindingHeading reviewCount={0} selectedCount={0} />
        {!canWrite ? <InlineNotice tone="info">{t('bindings.viewerNotice')}</InlineNotice> : null}
        <div className="grid gap-2 rounded-lg border border-[var(--color-panel-border)] p-2">
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton className="h-14 rounded-md" key={index} />
          ))}
        </div>
      </div>
    )
  }

  if (bindingsQuery.isError || !bindingsQuery.data) {
    return (
      <div aria-label={t('bindings.workspaceLabel')} className="grid gap-4">
        <BindingHeading reviewCount={0} selectedCount={0} />
        {!canWrite ? <InlineNotice tone="info">{t('bindings.viewerNotice')}</InlineNotice> : null}
        <ErrorState
          description={agentToolsErrorMessage(bindingsQuery.error, t, 'errors.loadBindings')}
          onRetry={() => void bindingsQuery.refetch()}
          title={t('errors.loadBindingsTitle')}
        />
      </div>
    )
  }

  return (
    <AgentToolBindingDraft
      bindings={bindingsQuery.data}
      canWrite={canWrite}
      key={bindingDraftKey(bindingsQuery.data, owner)}
      owner={owner}
    />
  )
}

function AgentToolBindingDraft({
  bindings,
  canWrite,
  owner,
}: {
  bindings: AgentToolBindings
  canWrite: boolean
  owner: AgentToolBindingOwner
}) {
  const { t } = useTranslation('adminAgentTools')
  const { showFeedback } = useFeedback()
  const queryClient = useQueryClient()
  const checkboxIdPrefix = useId()
  const initialSelected = useMemo(() => selectedState(bindings), [bindings])
  const [selected, setSelected] = useState<Set<string>>(() => new Set(initialSelected))
  // 漂移项必须在当前页面会话重新确认，不能沿用旧页面的确认状态。
  const [confirmedReviews, setConfirmedReviews] = useState<Set<string>>(new Set())
  const [drawerOpen, setDrawerOpen] = useState(false)

  const saveMutation = useMutation({
    mutationFn: ({ owner: savedOwner, payload }: SaveBindingVariables) =>
      savedOwner.agentType === 'self_agent'
        ? updateSelfAgentToolBindings(savedOwner.agentId, payload)
        : updateCoreAgentToolBindings(payload),
    onError: async (error, variables) => {
      if (
        error instanceof ApiError &&
        ['agent_tools.binding_conflict', 'agent_tools.definition_changed'].includes(
          error.errorCode ?? '',
        )
      ) {
        setDrawerOpen(false)
        await queryClient.invalidateQueries({ queryKey: ownerQueryKey(variables.owner) })
      }
      showFeedback({ message: agentToolsErrorMessage(error, t), tone: 'error' })
    },
    onSuccess: (result, variables) => {
      setDrawerOpen(false)
      queryClient.setQueryData(ownerQueryKey(variables.owner), result)
      showFeedback({ message: t('bindings.feedback.saved'), tone: 'success' })
    },
  })

  const tools = bindings.tools
  const hasDraftChanges = !sameSet(selected, initialSelected) || confirmedReviews.size > 0
  const reviewCount = tools.filter((tool) => tool.review_required).length

  const toolGroups = useMemo<SelectedToolGroup[]>(() => {
    const grouped = new Map<string, SelectedToolGroup>()
    for (const tool of tools) {
      const key = tool.source === 'builtin' ? 'builtin' : `mcp:${tool.connection?.id ?? 'unknown'}`
      const current = grouped.get(key)
      const group = current ?? {
        key,
        label:
          tool.source === 'builtin'
            ? t('bindings.picker.builtin')
            : tool.connection?.display_name.trim()
              ? tool.connection.display_name
              : t('bindings.picker.connectionUnknown'),
        source: tool.source,
        tools: [],
      }
      group.tools.push(tool)
      grouped.set(key, group)
    }
    return [...grouped.values()].sort((left, right) => {
      if (left.source !== right.source) {
        return left.source === 'builtin' ? -1 : 1
      }
      return left.label.localeCompare(right.label)
    })
  }, [t, tools])
  const selectedGroups = useMemo(
    () =>
      toolGroups
        .map((group) => ({
          ...group,
          tools: group.tools.filter((tool) => initialSelected.has(agentToolIdentityKey(tool))),
        }))
        .filter((group) => group.tools.length > 0),
    [initialSelected, toolGroups],
  )

  const resetDraft = () => {
    setSelected(new Set(initialSelected))
    setConfirmedReviews(new Set())
  }

  const closeDrawer = () => {
    resetDraft()
    setDrawerOpen(false)
  }

  const saveBindings = () => {
    try {
      const payload = buildAgentToolBindingsPayload(bindings, selected, confirmedReviews)
      saveMutation.mutate({ owner, payload })
    } catch (error) {
      if (error instanceof AgentToolSelectionError) {
        showFeedback({
          message: t(`bindings.validation.${error.code}`, { name: error.toolName ?? '' }),
          tone: 'error',
        })
        return
      }
      throw error
    }
  }

  return (
    <div aria-label={t('bindings.workspaceLabel')} className="grid gap-4">
      <BindingHeading
        action={
          canWrite && tools.length > 0 ? (
            <Button
              onClick={() => {
                resetDraft()
                setDrawerOpen(true)
              }}
              size="sm"
              variant="secondary"
            >
              {t('bindings.actions.manage')}
            </Button>
          ) : undefined
        }
        reviewCount={reviewCount}
        selectedCount={owner.agentType === 'self_agent' ? selected.size : initialSelected.size}
      />

      {!canWrite ? <InlineNotice tone="info">{t('bindings.viewerNotice')}</InlineNotice> : null}

      {tools.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[var(--color-panel-border)] px-5 py-8 text-center text-sm text-[var(--color-text-muted)]">
          {t('bindings.empty')}
        </div>
      ) : owner.agentType === 'self_agent' ? (
        <div className="grid gap-3">
          <div className="grid gap-2">
            {toolGroups.map((group, groupIndex) => (
              <details
                className="group overflow-hidden rounded-lg border border-[var(--color-divider)] bg-white"
                key={group.key}
              >
                <summary className="flex min-h-11 cursor-pointer list-none items-center gap-2.5 px-3.5 transition-colors marker:hidden hover:bg-[rgba(26,28,25,0.025)] [&::-webkit-details-marker]:hidden">
                  <ToolSourceMark source={group.source} />
                  <span className="min-w-0 flex-1 truncate text-sm font-semibold text-[var(--color-text)]">
                    {group.label}
                  </span>
                  <span className="text-xs tabular-nums text-[var(--color-text-muted)]">
                    {t('bindings.picker.groupToolCount', { count: group.tools.length })}
                  </span>
                  <svg
                    aria-hidden
                    className="size-4 shrink-0 text-[var(--color-text-muted)] transition-transform group-open:rotate-180"
                    fill="none"
                    viewBox="0 0 16 16"
                  >
                    <path
                      d="m4.5 6 3.5 3.5L11.5 6"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                    />
                  </svg>
                </summary>
                <div className="divide-y divide-[var(--color-divider)] border-t border-[var(--color-divider)] bg-[rgba(26,28,25,0.012)] pl-10">
                  {group.tools.map((tool, toolIndex) => (
                    <ToolOptionRow
                      checkboxId={`${checkboxIdPrefix}-${String(groupIndex)}-${String(toolIndex)}`}
                      confirmedReviews={confirmedReviews}
                      disabled={!canWrite}
                      key={agentToolIdentityKey(tool)}
                      onConfirmReview={(identity) => {
                        setConfirmedReviews((current) => new Set(current).add(identity))
                      }}
                      onSelectedChange={(next) => {
                        setSelected(next)
                        setConfirmedReviews(
                          (current) =>
                            new Set([...current].filter((identity) => next.has(identity))),
                        )
                      }}
                      selected={selected}
                      tool={tool}
                    />
                  ))}
                </div>
              </details>
            ))}
          </div>
          {canWrite ? (
            <div className="flex justify-end">
              <Button
                disabled={!hasDraftChanges}
                isLoading={saveMutation.isPending}
                onClick={saveBindings}
              >
                {t('bindings.actions.save')}
              </Button>
            </div>
          ) : null}
        </div>
      ) : selectedGroups.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[var(--color-panel-border)] px-5 py-8 text-center text-sm text-[var(--color-text-muted)]">
          {t('bindings.picker.noSelection')}
        </div>
      ) : (
        <div className="grid gap-2">
          {selectedGroups.map((group) => (
            <details
              className="group overflow-hidden rounded-lg border border-[var(--color-divider)] bg-white"
              key={group.key}
            >
              <summary className="flex min-h-11 cursor-pointer list-none items-center gap-2.5 px-3.5 transition-colors marker:hidden hover:bg-[rgba(26,28,25,0.025)] [&::-webkit-details-marker]:hidden">
                <ToolSourceMark source={group.source} />
                <span className="min-w-0 flex-1 truncate text-sm font-semibold text-[var(--color-text)]">
                  {group.label}
                </span>
                <span className="text-xs tabular-nums text-[var(--color-text-muted)]">
                  {t('bindings.picker.groupToolCount', { count: group.tools.length })}
                </span>
                <svg
                  aria-hidden
                  className="size-4 shrink-0 text-[var(--color-text-muted)] transition-transform group-open:rotate-180"
                  fill="none"
                  viewBox="0 0 16 16"
                >
                  <path
                    d="m4.5 6 3.5 3.5L11.5 6"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                  />
                </svg>
              </summary>
              <div className="divide-y divide-[var(--color-divider)] border-t border-[var(--color-divider)] bg-[rgba(26,28,25,0.012)]">
                {group.tools.map((tool) => (
                  <div
                    className="flex min-h-10 items-center gap-2 py-2 pr-4 pl-[54px]"
                    key={agentToolIdentityKey(tool)}
                  >
                    <span className="size-1.5 shrink-0 rounded-full bg-[var(--color-primary)]/55" />
                    <span className="min-w-0 flex-1 truncate text-sm text-[var(--color-text)]">
                      {tool.display_name}
                    </span>
                    {tool.review_required ? (
                      <Badge variant="warning">{t('bindings.status.reviewRequired')}</Badge>
                    ) : null}
                    {!tool.available ? (
                      <Badge variant="danger">{t('bindings.status.unavailable')}</Badge>
                    ) : null}
                  </div>
                ))}
              </div>
            </details>
          ))}
        </div>
      )}

      {drawerOpen ? (
        <ToolBindingDrawer
          confirmedReviews={confirmedReviews}
          hasDraftChanges={hasDraftChanges}
          isSaving={saveMutation.isPending}
          onClose={closeDrawer}
          onConfirmReview={(identity) => {
            setConfirmedReviews((current) => new Set(current).add(identity))
          }}
          onSave={saveBindings}
          onSelectedChange={(next) => {
            setSelected(next)
            setConfirmedReviews(
              (current) => new Set([...current].filter((identity) => next.has(identity))),
            )
          }}
          selected={selected}
          tools={tools}
        />
      ) : null}
    </div>
  )
}

export function AgentToolDraftSelector({
  error,
  isLoading,
  onRetry,
  onSelectedChange,
  selected,
  tools,
}: AgentToolDraftSelectorProps) {
  const { t } = useTranslation('adminAgentTools')
  const { showFeedback } = useFeedback()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerSelected, setDrawerSelected] = useState<Set<string>>(() => new Set(selected))
  const [confirmedReviews, setConfirmedReviews] = useState<Set<string>>(new Set())

  const selectedGroups = useMemo<SelectedToolGroup[]>(() => {
    const grouped = new Map<string, SelectedToolGroup>()
    for (const tool of tools) {
      if (!selected.has(agentToolIdentityKey(tool))) continue
      const key = tool.source === 'builtin' ? 'builtin' : `mcp:${tool.connection?.id ?? 'unknown'}`
      const group = grouped.get(key) ?? {
        key,
        label:
          tool.source === 'builtin'
            ? t('bindings.picker.builtin')
            : tool.connection?.display_name.trim()
              ? tool.connection.display_name
              : t('bindings.picker.connectionUnknown'),
        source: tool.source,
        tools: [],
      }
      group.tools.push(tool)
      grouped.set(key, group)
    }
    return [...grouped.values()].sort((left, right) => {
      if (left.source !== right.source) return left.source === 'builtin' ? -1 : 1
      return left.label.localeCompare(right.label)
    })
  }, [selected, t, tools])

  if (isLoading) {
    return (
      <div aria-label={t('bindings.workspaceLabel')} className="grid gap-4">
        <BindingHeading compact reviewCount={0} selectedCount={selected.size} />
        <div className="grid gap-2 rounded-lg border border-[var(--color-panel-border)] p-2">
          {Array.from({ length: 2 }, (_, index) => (
            <Skeleton className="h-14 rounded-md" key={index} />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div aria-label={t('bindings.workspaceLabel')} className="grid gap-4">
        <BindingHeading compact reviewCount={0} selectedCount={selected.size} />
        <ErrorState
          description={agentToolsErrorMessage(error, t, 'errors.loadBindings')}
          onRetry={onRetry}
          title={t('errors.loadBindingsTitle')}
        />
      </div>
    )
  }

  const openDrawer = () => {
    setDrawerSelected(new Set(selected))
    setConfirmedReviews(new Set())
    setDrawerOpen(true)
  }

  const confirmSelection = () => {
    try {
      buildAgentToolBindingsPayload(
        {
          agent_id: 'draft',
          agent_type: 'self_agent',
          selection_revision: 'draft',
          tools,
        },
        drawerSelected,
        confirmedReviews,
      )
      onSelectedChange(new Set(drawerSelected))
      setDrawerOpen(false)
    } catch (selectionError) {
      if (selectionError instanceof AgentToolSelectionError) {
        showFeedback({
          message: t(`bindings.validation.${selectionError.code}`, {
            name: selectionError.toolName ?? '',
          }),
          tone: 'error',
        })
        return
      }
      throw selectionError
    }
  }

  return (
    <div aria-label={t('bindings.workspaceLabel')} className="grid gap-4">
      <BindingHeading
        compact
        action={
          tools.length > 0 ? (
            <Button onClick={openDrawer} size="sm" variant="secondary">
              {t('bindings.actions.add')}
            </Button>
          ) : undefined
        }
        reviewCount={0}
        selectedCount={selected.size}
      />

      {tools.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[var(--color-panel-border)] px-5 py-8 text-center text-sm text-[var(--color-text-muted)]">
          {t('bindings.empty')}
        </div>
      ) : selectedGroups.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[var(--color-panel-border)] px-5 py-6 text-center text-sm text-[var(--color-text-muted)]">
          {t('bindings.picker.noSelection')}
        </div>
      ) : (
        <div className="grid gap-2">
          {selectedGroups.map((group) => (
            <details
              className="group overflow-hidden rounded-lg border border-[var(--color-divider)] bg-white"
              key={group.key}
            >
              <summary className="flex min-h-11 cursor-pointer list-none items-center gap-2.5 px-3.5 transition-colors marker:hidden hover:bg-[rgba(26,28,25,0.025)] [&::-webkit-details-marker]:hidden">
                <ToolSourceMark source={group.source} />
                <span className="min-w-0 flex-1 truncate text-sm font-semibold text-[var(--color-text)]">
                  {group.label}
                </span>
                <span className="text-xs tabular-nums text-[var(--color-text-muted)]">
                  {t('bindings.picker.groupToolCount', { count: group.tools.length })}
                </span>
              </summary>
              <div className="divide-y divide-[var(--color-divider)] border-t border-[var(--color-divider)] bg-[rgba(26,28,25,0.012)]">
                {group.tools.map((tool) => (
                  <div
                    className="flex min-h-10 items-center gap-2 py-2 pr-4 pl-[54px]"
                    key={agentToolIdentityKey(tool)}
                  >
                    <span className="size-1.5 shrink-0 rounded-full bg-[var(--color-primary)]/55" />
                    <span className="min-w-0 flex-1 truncate text-sm text-[var(--color-text)]">
                      {tool.display_name}
                    </span>
                  </div>
                ))}
              </div>
            </details>
          ))}
        </div>
      )}

      {drawerOpen ? (
        <ToolBindingDrawer
          confirmedReviews={confirmedReviews}
          hasDraftChanges={!sameSet(drawerSelected, selected)}
          isSaving={false}
          onClose={() => setDrawerOpen(false)}
          onConfirmReview={(identity) => {
            setConfirmedReviews((current) => new Set(current).add(identity))
          }}
          onSave={confirmSelection}
          onSelectedChange={(next) => {
            setDrawerSelected(next)
            setConfirmedReviews(
              (current) => new Set([...current].filter((identity) => next.has(identity))),
            )
          }}
          placement="left-secondary"
          saveLabel={t('bindings.actions.confirmSelection')}
          selected={drawerSelected}
          tools={tools}
        />
      ) : null}
    </div>
  )
}
